import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  startWith,
  tap,
  type Observable,
} from 'rxjs';
import { SubSink } from 'subsink';
import { ReloadButtonComponent } from '../../../shared/components/reload-button/reload-button.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import type { ActiveUser } from '../../../shared/models/api/user.model';
import type {
  EditVpnPhoneNumber,
  VpnPhoneNumber,
} from '../../../shared/models/api/vpn-phone-number.model';
import type { VpnSubscriptionType } from '../../../shared/models/api/vpn-subscription-type.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { UserService } from '../../../shared/services/api/user.service';
import { VpnPhoneNumberService } from '../../../shared/services/api/vpn-phone-number.service';
import { VpnSubscriptionTypeService } from '../../../shared/services/api/vpn-subscription-type.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type {
  EditVpnPhoneNumberData,
  EditVpnPhoneNumberFormValue,
} from './vpn-phone-number-edit.models';

@Component({
  selector: 'opa-vpn-phone-number-edit',
  imports: [
    CommonModule,
    PorscheDesignSystemModule,
    SpinnerComponent,
    ReloadButtonComponent,
    ReactiveFormsModule,
    LetDirective,
  ],
  templateUrl: './vpn-phone-number-edit.component.html',
  styleUrl: './vpn-phone-number-edit.component.scss',
})
export class VpnPhoneNumberEditComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly userService = inject(UserService);
  private readonly vpnSubscriptionTypeService = inject(VpnSubscriptionTypeService);
  private readonly vpnPhoneNumberService = inject(VpnPhoneNumberService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Input({ required: true }) vpnPhoneNumber: VpnPhoneNumber | undefined = undefined;
  @Output() dismiss = new EventEmitter<VpnPhoneNumber | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  data$: Observable<EditVpnPhoneNumberData> | undefined = undefined;

  ngOnChanges(changes: NgChanges<VpnPhoneNumberEditComponent>): void {
    if (changes.open && this.open && !this.data$) this.reloadData();
    if ((changes.open && this.open) || changes.vpnPhoneNumber) this.resetForm(this.vpnPhoneNumber);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(vpnPhoneNumber?: VpnPhoneNumber): void {
    this.dismiss.emit(vpnPhoneNumber);
  }

  reloadData(): void {
    this.data$ = this.getData$();
  }

  trackUser(index: number, user: ActiveUser): number {
    return user.id;
  }

  trackVpnSubscriptionType(index: number, vpnSubscriptionType: VpnSubscriptionType): number {
    return vpnSubscriptionType.id;
  }

  submit(): void {
    const vpnPhoneNumber = this.vpnPhoneNumber;
    if (!vpnPhoneNumber) return;

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const editVpnPhoneNumber = this.formValueToVpnPhoneNumber(formValue);
    this.subSink.sink = this.vpnPhoneNumberService
      .editVpnPhoneNumber(vpnPhoneNumber.id, editVpnPhoneNumber)
      .pipe(
        tap({
          next: (updatedVpnPhoneNumber) => {
            this.toastManager.addMessage({
              text: `VPN Phone Number ${updatedVpnPhoneNumber.vpnNumber} updated`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(updatedVpnPhoneNumber);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<EditVpnPhoneNumberFormValue>> {
    const form = this.formBuilder.group<ControlsOf<EditVpnPhoneNumberFormValue>>({
      userId: this.formBuilder.control(null, OpaValidators.required()),
      vpnNumber: this.formBuilder.control(null, OpaValidators.required()),
      fullNumber: this.formBuilder.control(null, OpaValidators.required()),
      subscriptionTypeId: this.formBuilder.control(null, OpaValidators.required()),
      additionalOptionActivation: this.formBuilder.control(false, OpaValidators.required()),
      additionalOptionDescription: this.formBuilder.control(null),
      contractObligation: this.formBuilder.control(false, OpaValidators.required()),
      contractStartDate: this.formBuilder.control(null),
      contractEndDate: this.formBuilder.control(null),
      active: this.formBuilder.control(false, OpaValidators.required()),
    });

    this.subSink.sink = form.controls.additionalOptionActivation.valueChanges
      .pipe(startWith(form.controls.additionalOptionActivation.value), distinctUntilChanged())
      .subscribe((value) => {
        const control = form.controls.additionalOptionDescription;
        if (value) control.setValidators(OpaValidators.required());
        else control.clearValidators();
        control.reset(control.value);
      });

    this.subSink.sink = form.controls.contractObligation.valueChanges
      .pipe(startWith(form.controls.contractObligation.value), distinctUntilChanged())
      .subscribe((value) => {
        const startControl = form.controls.contractStartDate;
        const endControl = form.controls.contractEndDate;
        if (value) {
          startControl.setValidators(OpaValidators.required());
          endControl.setValidators(OpaValidators.required());
        } else {
          startControl.clearValidators();
          endControl.clearValidators();
        }
        startControl.reset(startControl.value);
        endControl.reset(endControl.value);
      });
    return form;
  }

  private formValueToVpnPhoneNumber(form: EditVpnPhoneNumberFormValue): EditVpnPhoneNumber {
    const vpnPhoneNumber: EditVpnPhoneNumber = {
      userId: Number(form.userId!),
      vpnNumber: form.vpnNumber!,
      fullNumber: form.fullNumber!,
      subscriptionTypeId: Number(form.subscriptionTypeId!),
      additionalOptionActivation: form.additionalOptionActivation!,
      additionalOptionDescription: form.additionalOptionDescription!,
      contractObligation: form.contractObligation!,
      contractStartDate: form.contractStartDate!,
      contractEndDate: form.contractEndDate!,
      active: form.active!,
    };
    return vpnPhoneNumber;
  }

  private resetForm(vpnPhoneNumber?: VpnPhoneNumber): void {
    if (!vpnPhoneNumber) {
      this.form.reset();
      return;
    }

    this.form.reset({
      userId: String(vpnPhoneNumber.user.id),
      vpnNumber: vpnPhoneNumber.vpnNumber,
      fullNumber: vpnPhoneNumber.fullNumber,
      subscriptionTypeId: String(vpnPhoneNumber.subscriptionType.id),
      additionalOptionActivation: vpnPhoneNumber.additionalOptionActivation,
      additionalOptionDescription: vpnPhoneNumber.additionalOptionDescription,
      contractObligation: vpnPhoneNumber.contractObligation,
      contractStartDate: vpnPhoneNumber.contractStartDate,
      contractEndDate: vpnPhoneNumber.contractEndDate,
      active: vpnPhoneNumber.active,
    });
  }

  private getData$(): Observable<EditVpnPhoneNumberData> {
    const users$ = this.userService.getActiveUsers();
    const vpnSubscriptionTypes$ = this.vpnSubscriptionTypeService
      .getVpnSubscriptionTypes(null)
      .pipe(map((types) => types.content));
    const submitting$ = this.submitting.pipe(distinctUntilChanged());
    return combineLatest([users$, vpnSubscriptionTypes$, submitting$]).pipe(
      map(([users, vpnSubscriptionTypes, submitting]) => ({
        users,
        vpnSubscriptionTypes,
        submitting,
      })),
    );
  }
}
